/* eslint-disable max-lines */
import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { translate } from '@jsverse/transloco';
import { Action, createSelector, dispatch, type NgxsOnInit, State, type StateContext, Store } from '@ngxs/store';
import { Control, type Resolution, SmartTimeWindow, type StoreControl, ZoomSliderMode } from 'commons';
import type { EChartsOption } from 'echarts';
import type { XAXisOption } from 'echarts/types/dist/shared';
import { produce } from 'immer';
import { ChannelInfoService } from '../../../components/channel-color-dot/channel-info.service';
import { EchartsConfigService } from '../../../components/dtis-multilane-chart/services/echarts-config.service';
import type { DtisZoomEvent } from '../../../components/dtis-multilane-chart/zoomEvent';
import { CommentApiService } from '../../../services/api/comments.api.service';
import { TelemetryApiService } from '../../../services/api/telemetry.service';
import { SettingsState } from '../settings/settings.state';
import { TestPhaseState } from '../test-phase';
import {
  AnomalieActionHandler,
  ChannelHandlers,
  CommentHandler,
  ControlHandlers,
  DataActionHandler,
  getCurrentView,
  getViewIndex,
  LaneConfigHandler,
  ViewActionHandler,
  VisualizationHandlers,
} from './action-handler';
import * as a from './actions';
import type { MultiViewStateModel } from './MultiViewStateModel';
import type { SingleView, SingleViewSnapshot, ViewHistoryMeta } from './SingleView';

type Context = StateContext<MultiViewStateModel>;

@State<MultiViewStateModel>({
  name: 'multi_view_state',
  defaults: {
    views: [] as SingleView[],
    currentViewId: undefined,
    historyPanelOpen: false,
  },
})
@Injectable()
export class MultiViewState implements NgxsOnInit {
  telemetryService = inject(TelemetryApiService);
  commentService = inject(CommentApiService);
  store = inject(Store);
  channelInfoService = inject(ChannelInfoService);
  echartsConfigService = inject(EchartsConfigService);
  snackBar = inject(MatSnackBar);

  disableLoading = dispatch(a.ResetLoading);

  ngxsOnInit() {
    this.disableLoading();
  }

  static readonly getCurrentActiveLaneControls = createSelector([MultiViewState], (state: MultiViewStateModel) => {
    const view = getCurrentView(state);
    if (!view) {
      return [];
    }
    return view.lanes.flatMap((lane) => lane.controls.map((c) => new Control(c)));
  });

  static readonly getStyles = createSelector(
    [MultiViewState],
    (state: MultiViewStateModel) => getCurrentView(state)?.styles || { sensortable: {}, datatree: {} }
  );

  static readonly getDateRange = createSelector([MultiViewState], (state: MultiViewStateModel) => getCurrentView(state)?.dateRange || {});

  static readonly isCommentModeEnabled = createSelector([MultiViewState], (state: MultiViewStateModel) => getCurrentView(state)?.commentModeEnabled);

  static readonly isLoadingComments = createSelector([MultiViewState], (state: MultiViewStateModel) => getCurrentView(state)?.isLoadingComments ?? false);

  static readonly isTestPhasesVisible = createSelector([MultiViewState], (state: MultiViewStateModel) => getCurrentView(state)?.testPhasesVisible);

  static readonly getLaneControls = createSelector(
    [MultiViewState],
    (state: MultiViewStateModel) => getCurrentView(state)?.lanes.flatMap((lane) => lane.controls.map((c) => new Control(c))) || []
  );

  static readonly getKPIData = createSelector([MultiViewState], (state: MultiViewStateModel) => getCurrentView(state)?.kpiTable ?? {});

  static readonly getZoomSliderMode = createSelector(
    [MultiViewState],
    (state: MultiViewStateModel) => getCurrentView(state)?.zoomSliderMode ?? ZoomSliderMode.GLOBAL
  );

  static readonly getLanesCount = createSelector([MultiViewState], (state: MultiViewStateModel) => getCurrentView(state)?.lanes.length ?? 0);

  static readonly getComments = createSelector([MultiViewState], (state: MultiViewStateModel) => getCurrentView(state)?.comments ?? []);

  static readonly getEchartsConfig = createSelector(
    [MultiViewState],
    (state: MultiViewStateModel): EChartsOption => getCurrentView(state)?.echartsConfig ?? {}
  );

  static readonly getLoadingState = createSelector([MultiViewState], (state: MultiViewStateModel) => {
    const view = getCurrentView(state);
    if (!view) {
      return [];
    }
    return view.isLoading ?? [];
  });

  static readonly getSensorTableControlsOfCurrentView = createSelector(
    [MultiViewState],
    (state: MultiViewStateModel) => getCurrentView(state)?.sensorTable?.map((c) => new Control(c)) ?? []
  );

  static readonly getLanesOfCurrentView = createSelector([MultiViewState], (state: MultiViewStateModel) => getCurrentView(state)?.lanes ?? []);

  static readonly getViews = createSelector([MultiViewState], (state: MultiViewStateModel) => state.views);

  static readonly getLatestView = createSelector([MultiViewState], (state: MultiViewStateModel) => state.views.at(-1));

  static readonly getCurrentView = createSelector([MultiViewState], (state: MultiViewStateModel) => getCurrentView(state) as SingleView);

  static readonly getCurrentViewId = createSelector([MultiViewState], (state: MultiViewStateModel) => state.currentViewId);

  static readonly canUndo = createSelector([MultiViewState], (state: MultiViewStateModel) => (getCurrentView(state)?.history?.length ?? 0) > 0);

  static readonly canRedo = createSelector([MultiViewState], (state: MultiViewStateModel) => (getCurrentView(state)?.future?.length ?? 0) > 0);

  static readonly getViewHistory = createSelector([MultiViewState], (state: MultiViewStateModel) => getCurrentView(state)?.history ?? []);

  static readonly getViewFuture = createSelector([MultiViewState], (state: MultiViewStateModel) => getCurrentView(state)?.future ?? []);

  static readonly getViewHistoryMeta = createSelector([MultiViewState], (state: MultiViewStateModel) => getCurrentView(state)?.historyMeta ?? []);

  static readonly getViewFutureMeta = createSelector([MultiViewState], (state: MultiViewStateModel) => getCurrentView(state)?.futureMeta ?? []);

  static readonly isHistoryPanelOpen = createSelector([MultiViewState], (state: MultiViewStateModel) => state.historyPanelOpen ?? false);

  static readonly getSelectedPageType = createSelector([MultiViewState], (state: MultiViewStateModel) => getCurrentView(state)?.pageType as string);
  static readonly getCurrentActiveLane = createSelector([MultiViewState], (state: MultiViewStateModel) => getCurrentView(state)?.lanes.find((l) => l.isActive));

  /**
   * Get controls for a specific lane by lane ID
   * @param laneId - The ID of the lane, or 'all' for all controls
   */
  static readonly getControlsForLane = (laneId: string) =>
    createSelector([MultiViewState], (state: MultiViewStateModel) => {
      const currentView = getCurrentView(state);
      if (!currentView) {
        return [];
      }

      if (laneId === 'all') {
        return currentView.sensorTable?.map((c) => new Control(c)) ?? [];
      }

      const lane = currentView.lanes.find((l) => l.id === laneId);
      if (!lane) {
        return [];
      }

      // Filter sensor table controls to only include those in the selected lane
      return currentView.sensorTable?.filter((control) => lane.controls.some((laneControl) => laneControl.id === control.id)).map((c) => new Control(c)) ?? [];
    });

  /**
   * Get the current table view lane filter
   */
  static readonly getTableViewLaneFilter = createSelector(
    [MultiViewState],
    (state: MultiViewStateModel) => getCurrentView(state)?.tableViewLaneFilter ?? 'all'
  );

  static readonly getNewDataAvailableSince = createSelector([MultiViewState], (state: MultiViewStateModel) => getCurrentView(state)?.newDataAvailableSince);

  static readonly getLastTelemetryFetchedAt = createSelector([MultiViewState], (state: MultiViewStateModel) => getCurrentView(state)?.lastTelemetryFetchedAt);

  @Action(a.AddView) addView(ctx: Context, action: a.AddView) {
    ViewActionHandler.addView(ctx, action);
  }
  @Action(a.DeleteView) deleteView(ctx: Context, { id }: a.DeleteView) {
    ViewActionHandler.deleteView(ctx, { id });
  }
  @Action(a.SelectView) selectView(ctx: Context, { id }: a.SelectView) {
    ViewActionHandler.selectView(ctx, { id });
  }
  @Action(a.UpdateView) updateView(ctx: Context, { view }: a.UpdateView) {
    ViewActionHandler.updateView(ctx, { view });
  }
  @Action(a.ClearView) clearView(ctx: Context) {
    this.#captureHistory(ctx, a.ClearView.type);
    ViewActionHandler.clearView(ctx);
  }
  @Action(a.SetStyles) setStyles(ctx: Context, action: a.SetStyles) {
    ViewActionHandler.setStyles(ctx, action);
  }
  @Action(a.DuplicateView) DuplicateView(ctx: Context, { viewId }: a.DuplicateView) {
    ViewActionHandler.duplicateView(ctx, { viewId });
  }
  //-----------------------------------
  @Action(a.AddControlToCurrentView)
  addControlToCurrentViewSensorTable(ctx: Context, { control, lane, view }: a.AddControlToCurrentView) {
    this.#captureHistory(ctx, a.AddControlToCurrentView.type);
    ControlHandlers.addControl(ctx, { control, lane, view }, this.store);
  }

  @Action(a.AddBatchControlsToCurrentView)
  addBatchControlsToCurrentViewSensorTable(ctx: Context, { controls, lane, view }: a.AddBatchControlsToCurrentView) {
    this.#captureHistory(ctx, a.AddBatchControlsToCurrentView.type);
    ControlHandlers.addBatchControls(ctx, { controls, lane, view }, this.store);
  }

  @Action(a.BatchChangeChannels) batchChangeChannels(ctx: Context, { control, changeSetIds }: a.BatchChangeChannels) {
    ChannelHandlers.batchChangeChannels(ctx, { control, changeSetIds });
  }
  @Action(a.ResetLoading) resetLoading(ctx: Context) {
    ViewActionHandler.resetLoading(ctx);
  }
  @Action(a.SetLoading) setLoading(ctx: Context, { loadingChannels }: a.SetLoading) {
    ViewActionHandler.setLoading(ctx, loadingChannels);
  }
  @Action(a.UpdateControl) async updateControl(ctx: Context, { control }: a.UpdateControl) {
    await ControlHandlers.updateControl(ctx, { control });
  }
  @Action(a.RemoveControlFromCurrentView)
  removeControlFromCurrentViewSensorTable(ctx: Context, { control }: a.RemoveControlFromCurrentView) {
    this.#captureHistory(ctx, a.RemoveControlFromCurrentView.type);
    ViewActionHandler.removeControlFromCurrentView(ctx, control);
  }
  @Action(a.ToggleCommentMode)
  toggleCommentModeSensorTable(ctx: Context, action: a.ToggleCommentMode) {
    ViewActionHandler.toggleCommentMode(ctx, action);
  }
  @Action(a.ToggleTestPhasesVisibility)
  toggleTestPhasesVisibility(ctx: Context, action: a.ToggleTestPhasesVisibility) {
    ViewActionHandler.toggleTestPhasesVisibility(ctx, action);
  }
  @Action(a.SetLaneActive) setLaneActive(ctx: Context, { laneConfig }: a.SetLaneActive) {
    LaneConfigHandler.setLaneActive(ctx, laneConfig);
  }
  @Action(a.CreateVirtualChannel)
  createVirtualChannel(ctx: Context, { control }: a.CreateVirtualChannel) {
    this.#captureHistory(ctx, a.CreateVirtualChannel.type);
    ChannelHandlers.createVirtualChannel(ctx, { control });
  }
  @Action(a.ToggleConfidence)
  toggleConfidence(ctx: Context, { control }: a.ToggleConfidence) {
    VisualizationHandlers.toggleConfidence(ctx, { control });
  }
  @Action(a.OverrideEchartsOptionOnControl)
  overrideEchartsOptionOnControl(ctx: Context, { controlId, opts }: a.OverrideEchartsOptionOnControl) {
    VisualizationHandlers.overrideEchartsOptionOnControl(ctx, {
      controlId,
      opts,
    });
  }
  @Action(a.ToggleSensorVisibility)
  toggleSensorVisibility(ctx: Context, { control }: a.ToggleSensorVisibility) {
    this.#captureHistory(ctx, a.ToggleSensorVisibility.type);
    VisualizationHandlers.toggleVisibility(ctx, { control });
  }
  /**
   *
   * @param ctx
   * @param param1
   * @returns
   */
  @Action(a.GenerateEChartConfig) async generateEChartConfig(ctx: Context, { viewId }: a.GenerateEChartConfig) {
    // biome-ignore lint/suspicious/noExplicitAny: echarts config has no shared type
    const setState = (c: any) => {
      if (!c) {
        return console.error('No echarts config found');
      }
      return ctx.setState(
        produce(ctx.getState(), (draft) => {
          const view = draft.views[getViewIndex(ctx, viewId)];
          if (view) {
            view.echartsConfig = c;
          }
        })
      );
    };
    const currentView = getCurrentView(ctx.getState()) || ctx.getState()?.views?.find((v) => v.id === viewId);
    if (!currentView?.lanes) {
      return;
    }

    // Capture comment visibility at the start of this async action to detect
    // race conditions: multiple concurrent GenerateEChartConfig dispatches can
    // overwrite each other. If visibility changed during the await, the result
    // is stale and a newer dispatch will write the correct config instead.
    const capturedCommentModeEnabled = currentView.commentModeEnabled;
    const capturedCommentVisibility = new Map(currentView.comments.map((c) => [c.identifier, c.showInLane]));

    const gridConfig = this.store.selectSnapshot(SettingsState.gridConfig);
    try {
      const { config, series } = await this.echartsConfigService.generateConfig(
        {
          laneConfig: currentView.lanes,
          comments: currentView.comments,
          kpiTable: currentView.kpiTable,
          zoomSliderMode: currentView.zoomSliderMode,
          testPhasesVisible: currentView.testPhasesVisible,
          testPhases: this.store.selectSnapshot(TestPhaseState.getVisiblePhases),
          commentsVisible: currentView.commentModeEnabled,
        },
        currentView.zoomState,
        currentView.dateRange,
        {
          currentYAxisPanel: this.store.selectSnapshot(
            // biome-ignore lint/suspicious/noExplicitAny: editmode_state is not typed in the root state
            (state: any) => state.editmode_state?.currentYAxisPanel ?? null
          ),
          highlightedComment: this.store.selectSnapshot(
            // biome-ignore lint/suspicious/noExplicitAny: editmode_state is not typed in the root state
            (state: any) => state.editmode_state?.highlightedComment ?? null
          ),
        },
        gridConfig.gridHeight,
        gridConfig
      );
      // Only add the fallback ghost series when there is at least one xAxis;
      // emitting a series with no xAxis defined causes ECharts to throw
      // "xAxis '0' not found".
      const resolvedSeries = series.length === 0 && (config.xAxis as unknown[])?.length > 0 ? [{ data: [], type: 'line' }] : series;

      // Before writing, verify comment visibility hasn't changed since this
      // action started. A stale result from a concurrent action would
      // otherwise restore hidden comments as visible.
      const liveView = getCurrentView(ctx.getState()) || ctx.getState().views.find((v) => v.id === viewId);
      if (liveView) {
        const commentVisibilityChanged =
          liveView.commentModeEnabled !== capturedCommentModeEnabled ||
          liveView.comments.some((c) => capturedCommentVisibility.get(c.identifier) !== c.showInLane);
        if (commentVisibilityChanged) {
          // The toggle that changed visibility already dispatched a newer
          // GenerateEChartConfig — discard this stale result.
          return;
        }
      }

      setState({ ...config, series: resolvedSeries });
    } catch (error) {
      console.error('Error while generating ECharts config', error);
      setState({});
    }
  }
  /**
   *
   * @param ctx
   * @param param1
   * @returns
   */
  @Action(a.GetChannelData) getChannelData(ctx: Context, action: a.GetChannelData) {
    return DataActionHandler.getChannelDataH(ctx, action, this.store, this.telemetryService);
  }

  /**
   * Action to retrieve data for multiple channels in a single batch request
   * @param ctx - The context
   * @param action - The batch channel data action
   */
  @Action(a.GetBatchChannelData) getBatchChannelData(ctx: Context, action: a.GetBatchChannelData) {
    return DataActionHandler.getBatchChannelDataH(ctx, action, this.telemetryService, this.channelInfoService);
  }

  @Action(a.AddLaneConfig) addLaneConfig(ctx: Context, { laneConfig }: a.AddLaneConfig) {
    this.#captureHistory(ctx, a.AddLaneConfig.type);
    LaneConfigHandler.add(ctx, { laneConfig });
  }
  @Action(a.ReorderLanes) reorderLanes(ctx: Context, action: a.ReorderLanes) {
    this.#captureHistory(ctx, a.ReorderLanes.type);
    LaneConfigHandler.reorder(ctx, action);
  }
  @Action(a.UpdateLaneConfig) updateLaneConfig(ctx: Context, { laneConfig }: a.UpdateLaneConfig) {
    this.#captureHistory(ctx, a.UpdateLaneConfig.type);
    LaneConfigHandler.update(ctx, { laneConfig });
  }
  @Action(a.AddControlToLaneConfig) addControlToLaneConfig(ctx: Context, { control, lane }: a.AddControlToLaneConfig) {
    return LaneConfigHandler.addControlTo(ctx, {
      control,
      lane,
    });
  }
  @Action(a.RemoveControlFromLaneConfig) removeControlFromLaneConfig(ctx: Context, { control, lane }: a.RemoveControlFromLaneConfig) {
    this.#captureHistory(ctx, a.RemoveControlFromLaneConfig.type);
    LaneConfigHandler.removeControl(ctx, { control, lane });
  }
  @Action(a.RemoveLaneById) removeLaneById(ctx: Context, { id }: a.RemoveLaneById) {
    const viewIndex = getViewIndex(ctx);
    const lanes = ctx.getState().views[viewIndex]?.lanes;
    if (lanes && lanes.length <= 1) {
      this.snackBar.open(translate('ERROR.MIN_LANE_COUNT'), translate('Dismiss'), {
        duration: 3000,
      });
      return;
    }
    this.#captureHistory(ctx, a.RemoveLaneById.type);
    LaneConfigHandler.removeById(ctx, { id });
  }
  @Action(a.UpdateComment) updateComment(ctx: Context, { comment }: a.UpdateComment) {
    CommentHandler.update(ctx, { comment }, this.commentService);
    ctx.dispatch(a.DeactivateBrushTool);
  }
  @Action(a.CreateComment) createComment(ctx: Context, { comment }: a.CreateComment) {
    CommentHandler.create(ctx, { comment }, this.commentService);
    ctx.dispatch(a.DeactivateBrushTool);
  }
  @Action(a.CommentCallSUCCESS) CommentCallSUCCESS(ctx: Context) {
    ctx.dispatch(a.GenerateEChartConfig);
  }
  @Action(a.DeleteComment) deleteComment(ctx: Context, { id }: a.DeleteComment) {
    CommentHandler.remove(ctx, { id }, this.commentService);
  }
  @Action(a.ToggleCommentShowLane) toggleCommentShowLane(ctx: Context, { comment }: a.ToggleCommentShowLane) {
    CommentHandler.toggleOnLane(ctx, { comment });
  }

  @Action(a.ToggleCommentShowLaneLocal) toggleCommentShowLaneLocal(ctx: Context, { commentId, skipEcharts }: a.ToggleCommentShowLaneLocal) {
    CommentHandler.toggleOnLaneLocal(ctx, { commentId, skipEcharts }, this.commentService);
  }
  @Action(a.RefreshAllComments) refreshAllComments(ctx: Context) {
    return CommentHandler.refreshAllComments(ctx, this.commentService);
  }
  @Action(a.GetChannelComments) getChannelComments(ctx: Context, { channelId }: a.GetChannelComments) {
    return CommentHandler.getChannelComments(ctx, { channelId }, this.commentService);
  }
  @Action(a.GetAnomaliesSUCCESS) getAnomaliesSUCCESS(ctx: Context, { anomalies }: a.GetAnomaliesSUCCESS) {
    AnomalieActionHandler.getAnomalies_SUCCESS(ctx, { anomalies });
  }
  @Action(a.CheckMissingChannelData) checkMissingChannelData(ctx: Context) {
    return DataActionHandler.checkMissingChannelData(ctx, this.store);
  }
  @Action(a.CheckForNewTelemetryData) checkForNewTelemetryData(ctx: Context) {
    return DataActionHandler.checkForNewTelemetryDataH(ctx, this.telemetryService);
  }
  @Action(a.LoadNewTelemetryData) loadNewTelemetryData(ctx: Context) {
    return DataActionHandler.loadNewTelemetryDataH(ctx, this.store);
  }
  @Action(a.DismissNewTelemetryData) dismissNewTelemetryData(ctx: Context) {
    DataActionHandler.dismissNewTelemetryDataH(ctx);
  }
  @Action(a.SetZoom) setZoom(ctx: Context, { zoom }: a.SetZoom) {
    const view = getCurrentView(ctx.getState());
    if (!view || !zoom) {
      return console.warn('[setZoom] No view or zoom found');
    }
    if (zoom.dataZoomId?.includes('vertical')) {
      return this.#updateZoomStateByDatazoomId(view.zoomState, ctx, zoom);
    } else {
      return this.#handleXAxisZoom(ctx, zoom);
    }
  }
  @Action(a.ExportFile) exportFile(ctx: Context, action: a.ExportFile) {
    return DataActionHandler.exportViewAs(ctx, action, this.store, this.telemetryService);
  }
  @Action(a.SetDateRangeByData) setDateRangeByData(ctx: StateContext<MultiViewStateModel>) {
    DataActionHandler.setDateRangeByDataH(ctx);
  }
  @Action(a.SetZoomSliderMode) setZoomSliderMode(ctx: Context, { zoomSliderMode }: a.SetZoomSliderMode) {
    DataActionHandler.setZoomSliderMode(ctx, zoomSliderMode);
  }
  /**
   *
   * @param events
   * @param action
   * @param ctx
   * @param zoomUpdate
   */
  @Action(a.UndoViewChange)
  undoViewChange(ctx: Context): void {
    const state = ctx.getState();
    const viewIndex = getViewIndex(ctx);
    const currentView = state.views[viewIndex];
    if (!currentView) {
      return;
    }
    const history = currentView.history ?? [];
    const historyMeta = currentView.historyMeta ?? [];
    const future = currentView.future ?? [];
    const futureMeta = currentView.futureMeta ?? [];
    if (history.length === 0) {
      return;
    }
    const previousSnapshot = history.at(-1);
    if (!previousSnapshot) {
      return;
    }
    const previousMeta = historyMeta.at(-1) ?? {
      actionType: '',
      timestamp: Date.now(),
    };
    const currentSnapshot = this.#snapshotCurrentView(currentView);
    const newViews = state.views.map((v, i) =>
      i === viewIndex
        ? ({
            ...previousSnapshot,
            history: history.slice(0, -1),
            historyMeta: historyMeta.slice(0, -1),
            future: [currentSnapshot, ...future],
            futureMeta: [previousMeta, ...futureMeta],
          } as SingleView)
        : v
    );
    ctx.setState({ ...state, views: newViews });
    ctx.dispatch(new a.GenerateEChartConfig());
  }

  @Action(a.RedoViewChange)
  redoViewChange(ctx: Context): void {
    const state = ctx.getState();
    const viewIndex = getViewIndex(ctx);
    const currentView = state.views[viewIndex];
    if (!currentView) {
      return;
    }
    const history = currentView.history ?? [];
    const historyMeta = currentView.historyMeta ?? [];
    const future = currentView.future ?? [];
    const futureMeta = currentView.futureMeta ?? [];
    if (future.length === 0) {
      return;
    }
    const [nextSnapshot, ...remainingFuture] = future;
    const [nextMeta, ...remainingFutureMeta] = futureMeta;
    const currentSnapshot = this.#snapshotCurrentView(currentView);
    const newViews = state.views.map((v, i) =>
      i === viewIndex
        ? ({
            ...nextSnapshot,
            history: [...history, currentSnapshot].slice(-MultiViewState.MAX_HISTORY),
            historyMeta: [...historyMeta, nextMeta ?? { actionType: '', timestamp: Date.now() }].slice(-MultiViewState.MAX_HISTORY),
            future: remainingFuture,
            futureMeta: remainingFutureMeta,
          } as SingleView)
        : v
    );
    ctx.setState({ ...state, views: newViews });
    ctx.dispatch(new a.GenerateEChartConfig());
  }

  @Action(a.ToggleHistoryPanel)
  toggleHistoryPanel(ctx: Context): void {
    ctx.patchState({ historyPanelOpen: !ctx.getState().historyPanelOpen });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Private Helpers
  // ─────────────────────────────────────────────────────────────────────────────

  static readonly MAX_HISTORY = 20;

  /**
   * Strips large/transient fields from a single view before storing in the undo/redo stacks.
   * `echartsConfig` is omitted because it can be regenerated from the underlying
   * channel data already persisted in IndexedDB (channelData table).
   * `history`, `historyMeta`, `future`, `futureMeta` are excluded to avoid nesting stacks.
   */
  #snapshotCurrentView(view: SingleView): SingleViewSnapshot {
    // Destructure to exclude the per-view history stacks from the snapshot
    const { history, historyMeta, future, futureMeta, ...rest } = view as SingleView & {
      history: unknown;
      historyMeta: unknown;
      future: unknown;
      futureMeta: unknown;
    };
    return {
      ...rest,
      lanes: (view.lanes ?? []).map((l) => ({
        ...l,
        controls: [...(l.controls ?? [])],
        config: l.config
          ? {
              ...l.config,
              yAxises: l.config.yAxises?.map((y) => ({
                ...y,
                controls: [...(y.controls ?? [])],
              })),
            }
          : l.config,
        // biome-ignore lint/suspicious/noExplicitAny: spread produces plain objects; class methods not needed in snapshots
      })) as any,
      echartsConfig: {},
      isLoading: [],
    };
  }

  /**
   * Pushes a snapshot of the CURRENT view onto that view's undo history and clears its redo stack.
   * Called in every action handler that structurally mutates the current view.
   */
  #captureHistory(ctx: Context, actionType: string): void {
    const state = ctx.getState();
    const viewIndex = getViewIndex(ctx);
    const currentView = state.views[viewIndex];
    if (!currentView) {
      return;
    }
    const snapshot = this.#snapshotCurrentView(currentView);
    const updatedView = {
      ...currentView,
      history: [...(currentView.history ?? []), snapshot].slice(-MultiViewState.MAX_HISTORY),
      historyMeta: [...(currentView.historyMeta ?? []), { actionType, timestamp: Date.now() }].slice(-MultiViewState.MAX_HISTORY),
      future: [] as SingleViewSnapshot[],
      futureMeta: [] as ViewHistoryMeta[],
    };
    const newViews = state.views.map((v, i) => (i === viewIndex ? updatedView : v));
    ctx.setState({ ...state, views: newViews });
  }

  #updateZoomStateByDatazoomId(events: DtisZoomEvent[], ctx: StateContext<MultiViewStateModel>, zoomUpdate: DtisZoomEvent) {
    const sliderIndex = events.findIndex(({ dataZoomId: zid }) => zid === zoomUpdate?.id);

    if (sliderIndex < 0) {
      ctx.setState(
        produce(ctx.getState(), (draft) => {
          // biome-ignore lint/suspicious/noExplicitAny: zoomUpdate type differs slightly from DtisZoomEvent array element
          draft.views[getViewIndex(ctx)].zoomState.push(zoomUpdate as any);
          draft.views[getViewIndex(ctx)].lastZoomedId = zoomUpdate.dataZoomId;
        })
      );
    } else {
      ctx.setState(
        produce(ctx.getState(), (draft) => {
          draft.views[getViewIndex(ctx)].zoomState[sliderIndex] =
            // biome-ignore lint/suspicious/noExplicitAny: zoomUpdate type differs slightly from DtisZoomEvent array element
            zoomUpdate as any;
          draft.views[getViewIndex(ctx)].lastZoomedId = zoomUpdate.dataZoomId;
        })
      );
    }
    return { sliderIndex };
  }
  /**
   * Updates the control list data by dispatching actions to set loading state and fetch channel data.
   *
   * @param ctx - The state context of the MultiViewStateModel.
   * @param controls - An array of StoreControl objects representing the controls to update.
   * @param startTime - The start time for the data retrieval.
   * @param endTime - The end time for the data retrieval.
   * @param resolution - (Optional) The resolution for the data retrieval.
   * @param options - (Optional) Additional options for the data retrieval.
   */
  #updateControlListData(
    ctx: StateContext<MultiViewStateModel>,
    controls: StoreControl[],
    startTime: Date,
    endTime: Date,
    resolution?: Resolution,
    // biome-ignore lint/suspicious/noExplicitAny: options is a pass-through bag
    options?: any
  ) {
    ctx.dispatch(new a.SetLoading(controls.map(({ id }) => id)));

    // Use batch retrieval for better performance when handling multiple controls
    if (controls.length > 1) {
      const channelIds = controls.map(({ id }) => id);
      ctx.dispatch(
        new a.GetBatchChannelData(
          channelIds,
          {
            startTime,
            endTime,
            ...resolution,
          },
          options
        )
      );
    } else {
      // For single control, use the existing individual method
      controls.forEach(({ id }) => {
        ctx.dispatch(
          new a.GetChannelData(
            id,
            {
              startTime,
              endTime,
              ...resolution,
            },
            options
          )
        );
      });
    }
  }
  /**
   *
   * @param ctx
   * @param zoomUpdate
   * @returns
   */
  /**
   * Handles the zoom event on the X-axis.
   * Updates the zoom state and fetches the necessary data based on the zoom level.
   *
   * @param ctx - The state context of the MultiViewStateModel.
   * @param zoomUpdate - The zoom event data.
   */
  #handleXAxisZoom(ctx: StateContext<MultiViewStateModel>, zoomUpdate: DtisZoomEvent) {
    // Get the current view from the state
    const view = getCurrentView(ctx.getState());
    if (!view) {
      return console.warn('No view found');
    }

    // Destructure necessary properties from the current view
    const { zoomSliderMode, lanes, echartsConfig, zoomState, sensorTable } = view;

    // Convert and sort the start and end values of the zoom update
    this.#convertAndSortStartAndEndValues(zoomUpdate, echartsConfig);

    // Update the zoom state by data zoom ID
    const { sliderIndex } = this.#updateZoomStateByDatazoomId(zoomState, ctx, zoomUpdate);

    // Calculate the time difference and resolution
    const diff = Number(zoomUpdate.endValue) - Number(zoomUpdate.startValue);
    const resolution = new SmartTimeWindow(diff).getResolution();
    const zoomValueToDate = (value: string | number | Date | undefined) => new Date(Math.round(Number(value)));
    const zoomStartDate = zoomValueToDate(zoomUpdate.startValue);
    const zoomEndDate = zoomValueToDate(zoomUpdate.endValue);

    // Handle zoom slider mode for GLOBAL
    if (zoomSliderMode === ZoomSliderMode.GLOBAL) {
      ctx.dispatch(new a.BatchChangeChannels(resolution)).subscribe(() => {
        this.#updateControlListData(ctx, sensorTable, zoomStartDate, zoomEndDate, resolution, {
          replaceDate: true,
        });
      });
    }

    // Handle zoom slider mode for LANE
    if (zoomSliderMode === ZoomSliderMode.LANE) {
      const controls = lanes[sliderIndex]?.controls as Control[];
      if (!controls) {
        return;
      }
      const controlsIds = controls.map(({ id }) => id);

      ctx.dispatch(new a.BatchChangeChannels(resolution, controlsIds));
      this.#updateControlListData(ctx, controls, zoomStartDate, zoomEndDate);
    }
  }
  /**
   *
   * @param zoomUpdate
   * @param echartsConfig
   */
  #convertAndSortStartAndEndValues(
    zoomUpdate: DtisZoomEvent,
    // biome-ignore lint/suspicious/noExplicitAny: echarts config has no shared type
    echartsConfig: any
  ) {
    if ((!zoomUpdate.endValue || !zoomUpdate.startValue) && echartsConfig.xAxis) {
      zoomUpdate.startValue = (echartsConfig.xAxis as XAXisOption[])
        .map((x) => new Date(x.min as number).getTime())
        .filter((v) => !Number.isNaN(v))
        .sort((a, b) => a - b)[0];
      zoomUpdate.endValue = (echartsConfig.xAxis as XAXisOption[])
        .map((x) => new Date(x.max as number).getTime())
        .filter((v) => !Number.isNaN(v))
        .sort((a, b) => b - a)[0];
    }
  }

  @Action(a.SelectedPageType) handleSelectedView(ctx: Context, action: a.SelectedPageType) {
    ViewActionHandler.handleSelectedView(ctx, action);
  }

  @Action(a.SetTableViewLaneFilter) setTableViewLaneFilter(ctx: Context, action: a.SetTableViewLaneFilter) {
    ViewActionHandler.setTableViewLaneFilter(ctx, action);
  }
}
